import { useEffect, useState } from 'react';
import { useThree } from '@react-three/fiber';
import {
  attachFullscreenRepair,
  isFullscreen,
} from '../../core/fullscreen';

/**
 * Keeps R3F sizing + frameloop healthy when entering/exiting fullscreen.
 * Mount once inside every Canvas.
 */
export function FullscreenCanvasSync() {
  const gl = useThree((s) => s.gl);
  const setSize = useThree((s) => s.setSize);
  const invalidate = useThree((s) => s.invalidate);

  useEffect(() => {
    attachFullscreenRepair();

    let debounce = 0;
    const sync = () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        const parent = gl.domElement.parentElement;
        const w = parent?.clientWidth || window.innerWidth;
        const h = parent?.clientHeight || window.innerHeight;
        if (w > 1 && h > 1) {
          setSize(w, h);
          gl.setSize(w, h, false);
        }
        invalidate();
        requestAnimationFrame(() => invalidate());
      }, 16);
    };

    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync as EventListener);
    window.addEventListener('resize', sync);

    return () => {
      window.clearTimeout(debounce);
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener(
        'webkitfullscreenchange',
        sync as EventListener,
      );
      window.removeEventListener('resize', sync);
    };
  }, [gl, setSize, invalidate]);

  return null;
}

/** Chrome button label / aria for fullscreen toggle. */
export function useFullscreenFlag() {
  const [fs, setFs] = useState(false);
  useEffect(() => {
    const update = () => setFs(isFullscreen());
    update();
    document.addEventListener('fullscreenchange', update);
    document.addEventListener('webkitfullscreenchange', update as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', update);
      document.removeEventListener(
        'webkitfullscreenchange',
        update as EventListener,
      );
    };
  }, []);
  return fs;
}
